import { ref } from 'vue'
import assistantService from '@/helpers/api/assistant.service'
import billingV2Service from '@/helpers/api/billingV2.service'

// Tokens are revealed at this cadence instead of slammed onto the page the instant a WS frame
// arrives — upstream providers don't always stream delta-by-delta, so without this a reply can
// visibly "jump" in large bursts.
const REVEAL_INTERVAL_MS = 24
const REVEAL_MIN_CHUNK = 1
const REVEAL_CATCHUP_RATIO = 0.25

// Module-level singleton state: the chat (socket, history, in-flight turn) is global, not tied to
// any one component's lifecycle — the full-page view and the global slide-in drawer both read and
// drive the same conversation, and it must survive route navigation rather than reconnect/replay
// every time either one mounts.
// blocks: { type: 'text', text } | { type: 'widget', widgetId, widgetType, data, interactive }
const messages = ref([])          // [{ id, role, blocks, pending, streaming }]
const choices = ref(null)         // { prompt, choices:[{value,label}] } | null
const toolActivity = ref(null)    // string | null  (current tool chip)
const connected = ref(false)
const busy = ref(false)
const error = ref(null)
const conversationId = ref(null)
const conversationList = ref([])
const conversationsLoading = ref(false)
// AI assistant billing block (access tier, credit rate, monthly/trial free allowance) — same shape
// as BillingV2Controller::buildAiAssistantStatus. Shared so both the full-page view and the
// slide-in drawer show the same usage figures without fetching it twice.
const aiBilling = ref(null)
let billingLoaded = false
// Billing-target picker: which balance funds a NEW conversation. `billingTarget` is a resource_key
// (owner pays) or null (the acting user's own wallet). `billingTargets` are the cross-owner projects
// the user may charge to (empty for plain owners). Chosen only before the first message — once a
// conversation exists its payer is fixed server-side, so the picker is hidden thereafter.
const billingTargets = ref([])
const billingTarget = ref(null)
let billingTargetsLoaded = false

let socket = null
let assistantBuffer = null        // ref to the in-progress assistant message

function wsUrl() {
  if (process.env.VUE_APP_ASSISTANT_WS) return process.env.VUE_APP_ASSISTANT_WS
  // Fallback: same host as API server but ws scheme + :3200/ws (dev convenience).
  try {
    const u = new URL(process.env.VUE_APP_SERVER)
    const scheme = u.protocol === 'https:' ? 'wss:' : 'ws:'
    return `${scheme}//${u.hostname}:3200/ws`
  } catch (e) {
    return 'ws://localhost:3200/ws'
  }
}

function connect() {
  if (socket && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)) {
    return
  }
  error.value = null
  socket = new WebSocket(wsUrl())

  socket.onopen = () => { connected.value = true }
  socket.onclose = () => { connected.value = false; busy.value = false }
  socket.onerror = () => { error.value = 'ws_error' }
  socket.onmessage = (ev) => handleEvent(JSON.parse(ev.data))
}

// Appends to the trailing text block, or starts a new one — keeps block order correct when a
// widget lands between two bursts of streamed text.
function ensureTextBlock(msg) {
  const last = msg.blocks[msg.blocks.length - 1]
  if (last && last.type === 'text') return last
  const block = { type: 'text', text: '' }
  msg.blocks.push(block)
  return block
}

function startReveal(msg) {
  if (msg.revealTimer) return
  msg.revealTimer = setInterval(() => {
    if (!msg.pending) return
    const chunk = Math.max(REVEAL_MIN_CHUNK, Math.ceil(msg.pending.length * REVEAL_CATCHUP_RATIO))
    ensureTextBlock(msg).text += msg.pending.slice(0, chunk)
    msg.pending = msg.pending.slice(chunk)
  }, REVEAL_INTERVAL_MS)
}

function flushReveal(msg) {
  if (msg.revealTimer) {
    clearInterval(msg.revealTimer)
    msg.revealTimer = null
  }
  if (msg.pending) {
    ensureTextBlock(msg).text += msg.pending
    msg.pending = ''
  }
}

function handleEvent(event) {
  switch (event.type) {
    case 'token':
      if (!assistantBuffer) {
        assistantBuffer = { id: cryptoId(), role: 'assistant', blocks: [], pending: '', streaming: true, revealTimer: null }
        messages.value.push(assistantBuffer)
      }
      assistantBuffer.pending += event.text
      startReveal(assistantBuffer)
      break
    case 'tool_activity':
      // Internal marker the gateway uses to hand back the conversation id on first message.
      if (event.tool === '_conversation' && event.phase === 'end' && event.summary) {
        conversationId.value = event.summary
        loadConversationList()
        break
      }
      toolActivity.value = event.phase === 'start' ? event.tool : null
      break
    case 'choices':
      choices.value = { prompt: event.prompt, choices: event.choices }
      busy.value = false
      finalizeAssistant()
      break
    case 'widget':
      if (!assistantBuffer) {
        assistantBuffer = { id: cryptoId(), role: 'assistant', blocks: [], pending: '', streaming: true, revealTimer: null }
        messages.value.push(assistantBuffer)
      }
      // Flush any in-flight text first so the widget lands after it, not interleaved mid-word.
      flushReveal(assistantBuffer)
      assistantBuffer.blocks.push({
        type: 'widget',
        widgetId: event.widgetId,
        widgetType: event.widgetType,
        data: event.data,
        interactive: !!event.interactive,
      })
      break
    case 'turn_in_progress':
      // This socket reconnected while the previous turn (started on a now-dead connection) is
      // still running server-side. The gateway reattached that turn's output to us, so the
      // streamed tokens/message_complete for it will arrive right here — just keep showing the
      // "thinking" state instead of the old dead-end "already in progress" error.
      busy.value = true
      error.value = null
      break
    case 'awaiting_input':
      busy.value = false
      finalizeAssistant()
      break
    case 'message_complete':
      finalizeAssistant(event.text)
      busy.value = false
      toolActivity.value = null
      loadConversationList()
      break
    case 'error':
      error.value = event.message
      busy.value = false
      toolActivity.value = null
      finalizeAssistant()
      break
  }
}

function finalizeAssistant(finalText) {
  if (assistantBuffer) {
    if (finalText && !assistantBuffer.text && !assistantBuffer.pending) assistantBuffer.text = finalText
    flushReveal(assistantBuffer)
    assistantBuffer.streaming = false
    assistantBuffer = null
  }
}

function ensureConnected() {
  return new Promise((resolve) => {
    if (socket && socket.readyState === WebSocket.OPEN) return resolve()
    connect()
    const check = setInterval(() => {
      if (socket && socket.readyState === WebSocket.OPEN) { clearInterval(check); resolve() }
    }, 80)
    setTimeout(() => { clearInterval(check); resolve() }, 5000)
  })
}

async function sendMessage(text) {
  if (!text || busy.value) return
  await ensureConnected()
  messages.value.push({ id: cryptoId(), role: 'user', blocks: [{ type: 'text', text }], pending: '', streaming: false })
  choices.value = null
  error.value = null
  busy.value = true
  const frame = { type: 'user_message', conversationId: conversationId.value, text }
  // Billing target is honoured only on the frame that creates the conversation; the server ignores
  // it afterwards, so we only send it while there's no conversation yet.
  if (!conversationId.value && billingTarget.value) frame.billingTarget = billingTarget.value
  // Real browser language (not the UI locale, which is limited to en/ru and may not match what the
  // user actually speaks) — used server-side only as a fallback for short/ambiguous messages whose
  // language can't be told from the text itself.
  if (navigator.language) frame.lang = navigator.language
  socket.send(JSON.stringify(frame))
}

async function selectChoice(value, label) {
  await ensureConnected()
  messages.value.push({ id: cryptoId(), role: 'user', blocks: [{ type: 'text', text: label || value }], pending: '', streaming: false })
  choices.value = null
  busy.value = true
  error.value = null
  socket.send(JSON.stringify({ type: 'choice', conversationId: conversationId.value, value, label }))
}

function cancel() {
  if (socket && socket.readyState === WebSocket.OPEN && conversationId.value) {
    socket.send(JSON.stringify({ type: 'cancel', conversationId: conversationId.value }))
  }
}

function newChat() {
  if (assistantBuffer) flushReveal(assistantBuffer)
  assistantBuffer = null
  conversationId.value = null
  messages.value = []
  choices.value = null
  error.value = null
  toolActivity.value = null
  busy.value = false
}

function extractText(content) {
  if (!Array.isArray(content)) return ''
  return content.filter((b) => b.type === 'text').map((b) => b.text).join('')
}

// Reconstructs one bubble per turn from persisted docs, mirroring how the live view groups an
// entire turn (all its ReAct iterations plus any widgets) into a single `assistantBuffer` — see
// handleEvent()'s 'token'/'widget' cases. `messages` may hold several role:'assistant' docs per
// turn (one per iteration) and `widgets` holds zero or more card widgets for that turn; both are
// merged in `ts` order so a widget lands between the text that preceded/followed it live.
function buildTurns(messages, widgets) {
  const turnIds = []
  const byTurn = new Map()
  const ensureTurn = (turnId) => {
    let t = byTurn.get(turnId)
    if (!t) {
      t = { turnId, user: null, entries: [] }
      byTurn.set(turnId, t)
      turnIds.push(turnId)
    }
    return t
  }

  for (const m of messages) {
    const turn = ensureTurn(m.turn_id)
    if (m.role === 'user') {
      if (!turn.user) turn.user = extractText(m.content)
      continue
    }
    const text = extractText(m.content)
    if (text) turn.entries.push({ ts: m.ts, block: { type: 'text', text } })
  }
  for (const w of widgets) {
    const turn = ensureTurn(w.turn_id)
    turn.entries.push({
      ts: w.ts,
      block: { type: 'widget', widgetId: w.widgetId, widgetType: w.widgetType, data: w.data, interactive: !!w.interactive },
    })
  }

  const out = []
  for (const turnId of turnIds) {
    const turn = byTurn.get(turnId)
    if (turn.user) {
      out.push({ id: cryptoId(), role: 'user', blocks: [{ type: 'text', text: turn.user }], pending: '', streaming: false })
    }
    if (turn.entries.length) {
      turn.entries.sort((a, b) => new Date(a.ts) - new Date(b.ts))
      out.push({
        id: cryptoId(),
        role: 'assistant',
        blocks: turn.entries.map((e) => e.block),
        pending: '',
        streaming: false,
      })
    }
  }
  return out
}

async function loadConversationList() {
  conversationsLoading.value = true
  try {
    const data = await assistantService.conversations()
    conversationList.value = data.items || []
  } catch (e) {
    // Best-effort sidebar; a failed fetch shouldn't disrupt an active chat.
  } finally {
    conversationsLoading.value = false
  }
}

async function openConversation(id) {
  if (busy.value || id === conversationId.value) return
  const data = await assistantService.conversation(id)
  if (assistantBuffer) flushReveal(assistantBuffer)
  assistantBuffer = null
  conversationId.value = id
  messages.value = buildTurns(
    data.messages.filter((m) => m.role === 'user' || m.role === 'assistant'),
    data.widgets || [],
  )
  choices.value = null
  error.value = null
  toolActivity.value = null
}

async function loadBilling() {
  if (billingLoaded) return
  billingLoaded = true
  try {
    const data = await billingV2Service.getStatus()
    if (data?.ai_assistant) aiBilling.value = data.ai_assistant
  } catch (e) {
    billingLoaded = false
  }
}

async function loadBillingTargets() {
  if (billingTargetsLoaded) return
  billingTargetsLoaded = true
  try {
    const data = await billingV2Service.getAiAssistantTargets()
    billingTargets.value = data?.targets || []
  } catch (e) {
    billingTargetsLoaded = false
  }
}

/** Set the payer for the next new conversation: a resource_key (owner pays) or null (my wallet). */
function setBillingTarget(key) {
  billingTarget.value = key || null
}

function cryptoId() {
  return (window.crypto?.randomUUID && window.crypto.randomUUID()) || String(Date.now() + Math.random())
}

const api = {
  messages, choices, toolActivity, connected, busy, error,
  conversationId, conversationList, conversationsLoading, aiBilling,
  billingTargets, billingTarget,
  connect, sendMessage, selectChoice, cancel,
  newChat, loadConversationList, openConversation, loadBilling,
  loadBillingTargets, setBillingTarget,
}

/**
 * Returns the single shared chat client. Every caller (the full-page view, the global drawer)
 * gets the same refs/socket — there is exactly one assistant conversation per browser session.
 */
export function useAssistantChat() {
  return api
}
